import { ChangeEvent } from 'react'

import { faBell, faShieldHalved, faUser } from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Box, Tab, Typography } from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import { TabContext, TabList, TabPanel } from '@material-ui/lab'
import { useCurrentHeight } from '@scoir/height-observer'
import { Redirect, Route, RouteComponentProps, Switch, matchPath } from 'react-router'

import SYSTEM_PERMISSIONS from 'common/system-permissions'
import useTitle from 'common/use-title'
import { TOP_NAVIGATION_HEIGHT_ID } from 'components/HeaderNavigation'
import { useHistory, useLocation } from 'components/ReactRouterProvider/context'
import { useAppSelector } from 'store'
import { hasPermission } from 'store/user'

import EmailNotificationsContent from './EmailNotificationsContent'
import ProfileContent from './ProfileContent'
import SecurityContent from './SecurityContent'

const SIDEBAR_WIDTH = 300

const useStyles = makeStyles((theme) => ({
  root: {
    backgroundColor: theme.palette.common.white,
    display: 'flex',
    flex: 1,
  },
  tabList: (props: { navHeight: number }) => ({
    position: 'fixed',
    top: props.navHeight,
    left: 0,
    width: SIDEBAR_WIDTH,
    height: `calc(100vh - ${props.navHeight}px)`,
    overflowY: 'auto',
    borderRight: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.common.white,
    zIndex: 9,
    '& .MuiTabs-fixed': {
      margin: theme.spacing(3),
    },
    '& .Mui-selected': {
      backgroundColor: '#EBF5F9',
    },
  }),
  tabLabel: {
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    gridGap: theme.spacing(4),
  },
  tabPanel: {
    flex: 1,
    marginLeft: SIDEBAR_WIDTH,
  },
}))

const Settings = ({ match }: RouteComponentProps) => {
  useTitle(`Scoir - Settings`)
  const navHeight = useCurrentHeight(TOP_NAVIGATION_HEIGHT_ID) || 0
  const classes = useStyles({ navHeight })
  const history = useHistory()
  const location = useLocation()

  const canManageDocuments = useAppSelector((state) =>
    hasPermission(state, SYSTEM_PERMISSIONS.PERFORM_ADMISSIONS_TASKS),
  )
  const canViewHandraisers = useAppSelector((state) => hasPermission(state, SYSTEM_PERMISSIONS.VIEW_STUDENT_INTERESTS))
  const isPremiumExplorerEnabled = useAppSelector(
    (state) => state.college.record?.Features?.SupportsPremiumHighSchoolExplorer || false,
  )
  const canReceiveTargetHighSchoolEmails =
    useAppSelector((state) => hasPermission(state, SYSTEM_PERMISSIONS.PERFORM_VISIT_MANGEMENT_TASKS)) &&
    isPremiumExplorerEnabled

  const showEmailNotifications = canManageDocuments || canViewHandraisers || canReceiveTargetHighSchoolEmails

  const tabMatch = matchPath<{ tab: string }>(location.pathname, { path: `${match.path}/:tab` })
  const activeTab = tabMatch?.params.tab
  const value = () => {
    switch (activeTab) {
      case 'email-notifications':
        return showEmailNotifications ? 'email-notifications' : 'profile'
      case 'security':
        return 'security'
      default:
        return 'profile'
    }
  }

  const handleChange = (_: ChangeEvent<unknown>, newValue: string) => {
    history.replace(`${match.url}/${newValue}`)
  }

  return (
    <Box className={classes.root}>
      <TabContext value={value()}>
        <TabList
          orientation="vertical"
          onChange={handleChange}
          aria-label="settings navigation"
          className={classes.tabList}
        >
          <Typography variant="h6">Settings</Typography>
          {[
            { value: 'profile', icon: faUser, label: 'Profile', show: true },
            { value: 'email-notifications', icon: faBell, label: 'Email Notifications', show: showEmailNotifications },
            { value: 'security', icon: faShieldHalved, label: 'Security', show: true },
          ]
            .filter(({ show }) => show)
            .map(({ value: tabValue, icon, label }) => (
              <Tab
                key={tabValue}
                label={
                  <div className={classes.tabLabel}>
                    <FontAwesomeIcon icon={icon} /> <Typography>{label}</Typography>
                  </div>
                }
                value={tabValue}
              />
            ))}
        </TabList>
        <Switch>
          <Route path={`${match.path}/profile`}>
            <TabPanel
              value="profile"
              className={classes.tabPanel}
            >
              <ProfileContent />
            </TabPanel>
          </Route>
          {showEmailNotifications && (
            <Route path={`${match.path}/email-notifications`}>
              <TabPanel
                value="email-notifications"
                className={classes.tabPanel}
              >
                <EmailNotificationsContent
                  canManageDocuments={canManageDocuments}
                  canViewHandraisers={canViewHandraisers}
                  canReceiveTargetHighSchoolEmails={canReceiveTargetHighSchoolEmails}
                />
              </TabPanel>
            </Route>
          )}
          <Route path={`${match.path}/security`}>
            <TabPanel
              value="security"
              className={classes.tabPanel}
            >
              <SecurityContent />
            </TabPanel>
          </Route>
          <Redirect to={`${match.url}/profile`} />
        </Switch>
      </TabContext>
    </Box>
  )
}

export default Settings
